import { CHARTS_STORE_NAME, IStore } from './store';
import { useDispatch, useSelector } from 'react-redux';
import { API_NAMES, ChartType, chartTypeApiMap, fetchMarketHour } from './Actions';
import { LocationType, useFrontendLocationSelector, useFrontendSubscriptionStatus } from '@cmctechnology/webinvest-store-frontend';
import { useEffect, useState } from 'react';
import { getChartKeyFromRequest } from './charts/simple/utils';
import { getChartProKeyFromRequest } from './charts/pro/utils';
import { IChartRequest, IChartSummary } from './charts/simple/models';
import { IChartProRequest, IChartProSummary } from './charts/pro/models';
import { useInstrument } from '@cmctechnology/webinvest-store-instruments';
import { subscribe, unsubscribe } from './Actions';
import { DEFAULT_TIMEZONE } from './utils';

export const useChartStatsSelector = (location: LocationType, chartType: ChartType = 'simple') => {
  const feLocation = <string>useFrontendLocationSelector(location, CHARTS_STORE_NAME, chartTypeApiMap[chartType]);

  return chartType === 'simple'
    ? useSelector((store: IStore) => (store.charts?.[feLocation] as IChartSummary)?.stats)
    : useSelector((store: IStore) => (store.charts?.[feLocation] as IChartProSummary)?.proChartData);
};

export const useCharts = <T extends ChartType>(
  location: LocationType,
  request: T extends 'simple' ? IChartRequest : IChartProRequest,
  chartType: T = 'simple' as T,
  pollTimeout?: number
) => {
  const marketHour = useSelector((store: IStore) => store.charts.marketHour);
  const exchangeId = useInstrument(request.instrumentCode, undefined, false)?.data?.details?.exchangeId;
  const timezone = marketHour?.[exchangeId]?.IANATimeZone || DEFAULT_TIMEZONE;

  const key = chartType === 'simple' ? getChartKeyFromRequest(request as IChartRequest) : getChartProKeyFromRequest(request as IChartProRequest, timezone);
  const charts = useSelector((store: IStore) => store.charts[key]);

  const dispatch = useDispatch();

  const apiName = chartType === 'simple' ? API_NAMES.ChartDataVeGet1 : API_NAMES.ChartDataProChartGet1;
  const { polling, error, fetching } = useFrontendSubscriptionStatus(CHARTS_STORE_NAME, apiName, key);
  const [prevRequest, setPrevRequest] = useState(request);

  useEffect(() => {
    dispatch<any>(subscribe(location, request, timezone, chartType, pollTimeout));

    return () => {
      dispatch<any>(unsubscribe(location, request, timezone, chartType));
    };
  }, []);

  useEffect(() => {
    if (request.instrumentCode !== prevRequest.instrumentCode || request.timeframe !== prevRequest.timeframe || request.interval !== prevRequest.interval) {
      dispatch<any>(subscribe(location, request, timezone, chartType, pollTimeout));
      dispatch<any>(unsubscribe(location, prevRequest, timezone, chartType));
      setPrevRequest(request);
    }

    if (!marketHour && chartType === 'pro') dispatch<any>(fetchMarketHour());
  }, [request.instrumentCode, request.timeframe, request.interval]);

  return {
    loading: fetching && !charts,
    polling,
    error,
    data: charts,
    fetching
  };
};
